import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __MANAGEMENT_API_URL__, __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { CirculationArea } from 'shell/models/circulation-area';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { Service } from 'shell/models/service';
import FormService, { ServiceForm } from 'system/components/services/form';
import { CommonServiceCrumbs } from '../constants';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import {
  transformerService,
  transformerTownHall,
} from 'system/components/services/utils/transformer-service';
import { transformerCirculationArea } from 'system/components/services/utils/transformer-circulation-area';
import { MunicipalityVM } from 'system/view-models/municipality';
import { CirculationAreaClient } from 'shell/view-models/circulation-area-client';

const ServiceEditPage = (): React.ReactElement => {
  const params = useParams();

  const updateService = useHttpClient(`${__MANAGEMENT_API_URL__}/servizi/${params.id}`, {
    options: {
      errorCatching: true,
      method: 'PUT',
      loading: true,
      data: {},
    },
  });

  const [townHall, getTownHall] = useHttpFetch(`${__TOPONYMY_API_URL__}/municipi`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerTownHall,
  });

  const [service, getService] = useHttpFetch<HttpResponse<Service>, ServiceForm>(
    `${__MANAGEMENT_API_URL__}/servizi/${params.id}`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformerService,
    }
  );

  const [circulationArea, getCirculationArea] = useHttpFetch<
    HttpResponse<CirculationArea>,
    CirculationAreaClient
  >(`${__TOPONYMY_API_URL__}/areaCircolazione`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerCirculationArea,
  });

  const serviceInitialValues: ServiceForm = useMemo(() => {
    if (!service.data) {
      return {
        code: '',
        name: '',
        streetNumber: '',
        shortDescription: '',
        phoneNumber: '',
        area: null,
        type: '',
        civicRepresentative: '',
        contextLastUpdate: '',
        zone: null,
      };
    }

    return {
      ...(service.data as ServiceForm),
      area: circulationArea.data as CirculationAreaClient,
      zone: townHall.data as MunicipalityVM,
    };
  }, [service.data, circulationArea.data, townHall.data]);

  const navigate = useNavigate();
  const onSubmit = useCallback(
    (formValues: ServiceForm | undefined) => {
      (
        updateService({
          data: {
            descrizioneBreve: formValues?.shortDescription,
            descrizione: formValues?.name,
            telefono: formValues?.phoneNumber || undefined,
            pec: formValues?.pec || undefined,
            peo: formValues?.peo || undefined,
            areaCircolazione: formValues?.area?.code,
            key: {
              codiceServizio: formValues?.code,
            },
            esponente: formValues?.civicRepresentative,
            note: formValues?.contextLastUpdate,
            zona: formValues?.zone?.code,
            civico: formValues?.streetNumber,
            gestioneCassa: formValues?.managementCash ? 'S' : undefined,
          },
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/sistema/servizi');
      });
    },
    [updateService, navigate]
  );

  useEffect(() => {
    const castGetService = getService() as ResponsePromise<HttpResponse<Service>, ServiceForm>;
    castGetService.then((response) => {
      const responseService = response as ServiceForm;
      if (responseService.zoneCode) {
        getTownHall({
          url: `/${responseService.zoneCode}`,
        });
      }
      if (responseService?.areaCircolazione) {
        getCirculationArea({
          url: `/${responseService?.areaCircolazione?.toString()}`,
        });
      }
    });
  }, [params.id]);

  const loadCirculationArea = serviceInitialValues.areaCircolazione
    ? circulationArea.isLoading || circulationArea.pristine || circulationArea.hasErrors
    : false;

  const loadingService =
    service.isLoading || service.pristine || service.hasErrors || loadCirculationArea;

  return (
    <Fragment>
      <Breadcrumbs
        items={[
          ...CommonServiceCrumbs,
          {
            name: 'Modifica Servizio',
          },
        ]}
        base="sistema"
      />
      {loadingService ? null : (
        <FormService
          title="Modifica Servizio"
          onOk={onSubmit}
          onCancel={() => {
            navigate('/sistema/servizi');
          }}
          initialValues={serviceInitialValues}
        />
      )}
    </Fragment>
  );
};

ServiceEditPage.displayName = 'Town Hall Edit page component';

export default ServiceEditPage;
